'use client'

import { useEffect, useMemo, useRef } from 'react'
import { useAccessState } from '@/hooks/useAccessState'
import { withPostHog } from '@/lib/posthog/init'

/**
 * Pushes subscription-derived person properties to PostHog whenever they change.
 * Mounted inside `app/(app)/layout.tsx` rather than the root layout — the underlying
 * useAccessState() hook calls /api/access/status on mount, which we don't want firing
 * on public/landing routes. The authed-shell mount also shares React Query's cache with
 * the layout's existing useAccessState() call, so this mount adds no extra fetch.
 *
 * Lives in its own module (not PostHogClient.tsx) so the root layout's PostHogClient
 * import doesn't drag useAccessState → useSession → the Supabase browser client into
 * every page's first-load chunk.
 *
 * Backbone for cohort filtering — project-level test_account_filters use is_admin=true
 * to drop admin traffic, and trial-cohort insights filter on subscription_status='trial'.
 */
export function PostHogPersonProperties() {
  const { accessState, loading } = useAccessState()

  // Derived subscription_status: priority-ordered single string. Drives most
  // cohort filters cleanly — "all trial users", "all canceled users", etc.
  const subscriptionStatus = useMemo(() => {
    if (loading) return null
    if (accessState.isAdmin) return 'admin'
    const records = accessState.accessRecords ?? []
    // Active trials are stored as is_trial=true + status='active'. Check is_trial
    // first so trials don't collapse into the 'active' bucket.
    if (records.some((r) => r.is_trial && r.status === 'active')) return 'trial'
    if (records.some((r) => r.status === 'active')) return 'active'
    if (records.some((r) => r.status === 'past_due')) return 'past_due'
    if (records.length > 0 && records.every((r) => r.status === 'canceled')) return 'canceled'
    return 'none'
  }, [accessState, loading])

  // Both the dedup signature and the actual write use the SAME sorted snapshot so
  // we can't drop a write because a same-set-different-order array fooled the dedup.
  const sortedSports = useMemo(
    () => [...accessState.sportsAccess].sort(),
    [accessState.sportsAccess],
  )

  const propsSignature = useMemo(() => {
    if (loading) return null
    return JSON.stringify({
      subscription_status: subscriptionStatus,
      is_admin: accessState.isAdmin,
      trial_used: accessState.trialUsed,
      sports_with_access: sortedSports,
    })
  }, [accessState.isAdmin, accessState.trialUsed, subscriptionStatus, sortedSports, loading])

  const lastPushedSignatureRef = useRef<string | null>(null)

  useEffect(() => {
    if (propsSignature === null) return
    if (lastPushedSignatureRef.current === propsSignature) return
    // withPostHog handles the init race with the root PostHogClient mount —
    // the write queues behind the SDK load if this effect wins. Mark the
    // signature pushed inside the callback: if the SDK load fails the queued
    // write never runs, and a ref set up-front would suppress every retry.
    withPostHog((ph) => {
      ph.setPersonProperties({
        subscription_status: subscriptionStatus,
        is_admin: accessState.isAdmin,
        trial_used: accessState.trialUsed,
        sports_with_access: sortedSports,
      })
      lastPushedSignatureRef.current = propsSignature
    })
  }, [propsSignature, accessState.isAdmin, accessState.trialUsed, subscriptionStatus, sortedSports])

  return null
}
